import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { transactionsAPI, adminAPI } from '../services/api'
import { toast } from 'react-hot-toast'

export const useTransactionActions = () => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const exportTransactions = async (filters = {}) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()

      // Apply filters
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type)
      }
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status)
      }
      if (filters.dateRange) {
        if (filters.dateRange.startDate) {
          params.append('dateFrom', filters.dateRange.startDate.toISOString().split('T')[0])
        }
        if (filters.dateRange.endDate) {
          params.append('dateTo', filters.dateRange.endDate.toISOString().split('T')[0])
        }
      }
      if (filters.amountRange && Array.isArray(filters.amountRange)) {
        if (filters.amountRange[0] !== 0) {
          params.append('amountMin', filters.amountRange[0].toString())
        }
        if (filters.amountRange[1] !== 1000) {
          params.append('amountMax', filters.amountRange[1].toString())
        }
      }

      const response = await transactionsAPI.exportTransactions(Object.fromEntries(params))

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `transactions-export-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('İşlemler başarıyla dışa aktarıldı')
    } catch (error) {
      const message = error.response?.data?.message || 'Dışa aktarma sırasında hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const refundTransaction = async (transactionId, reason) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post(`/transactions/${transactionId}/refund`, {
        reason,
      })

      // Invalidate and refetch transactions data
      queryClient.invalidateQueries(['admin', 'transactions'])
      
      toast.success('İşlem başarıyla iade edildi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'İade işlemi sırasında hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const cancelTransaction = async (transactionId, reason) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post(`/transactions/${transactionId}/cancel`, {
        reason,
      })

      // Invalidate and refetch transactions data
      queryClient.invalidateQueries(['admin', 'transactions'])
      
      toast.success('İşlem başarıyla iptal edildi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'İptal işlemi sırasında hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const processTransaction = async (transactionId) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post(`/transactions/${transactionId}/process`)

      // Invalidate and refetch transactions data
      queryClient.invalidateQueries(['admin', 'transactions'])
      
      toast.success('İşlem başarıyla işlendi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'İşlem işlenirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const addTransactionNote = async (transactionId, note) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post(`/transactions/${transactionId}/notes`, {
        note,
      })

      // Invalidate and refetch transaction details
      queryClient.invalidateQueries(['admin', 'transaction-details', transactionId])
      
      toast.success('İşlem notu başarıyla eklendi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Not eklenirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const generateTransactionReport = async (filters = {}) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()

      // Apply filters
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type)
      }
      if (filters.dateRange) {
        if (filters.dateRange.startDate) {
          params.append('dateFrom', filters.dateRange.startDate.toISOString().split('T')[0])
        }
        if (filters.dateRange.endDate) {
          params.append('dateTo', filters.dateRange.endDate.toISOString().split('T')[0])
        }
      }

      const response = await adminAPI.get(`/transactions/report?${params.toString()}`)
      
      toast.success('Rapor başarıyla oluşturuldu')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Rapor oluşturulurken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return {
    exportTransactions,
    refundTransaction,
    cancelTransaction,
    processTransaction,
    addTransactionNote,
    generateTransactionReport,
    isLoading,
  }
}